import { IconsVariant } from "@/Components/Icon/Icon";

export interface ImageGuidelines {
  recommendedWidth?: number;
  recommendedHeight?: number;
  /** Below this we warn that the image may look blurry. */
  minWidth?: number;
  minHeight?: number;
  /** width / height. Unset means any aspect ratio is fine. */
  aspectRatio?: number;
  /** Allowed deviation from the aspect ratio before we warn about cropping. */
  aspectRatioTolerance?: number;
  maxFileSize: number;
  acceptedTypes: string[];
  /** Extra tips shown in the guidelines panel (translation keys). */
  tips?: { icon: IconsVariant; key: string }[];
  guidelinesTitleKey?: string;
  dropHintKey?: string;
}

/** Any image, only type and size are checked. */
export const DEFAULT_IMAGE_GUIDELINES: ImageGuidelines = {
  maxFileSize: 10 * 1024 * 1024,
  acceptedTypes: ["image/png", "image/jpeg", "image/webp"],
  dropHintKey: "imageUpload.dropHint",
};

/**
 * Guidelines for product (dish) photos. Product images are shown square in
 * the menu, POS and kiosk, so a square photo is never cropped.
 */
export const PRODUCT_IMAGE_GUIDELINES: ImageGuidelines = {
  ...DEFAULT_IMAGE_GUIDELINES,
  recommendedWidth: 1200,
  recommendedHeight: 1200,
  minWidth: 600,
  minHeight: 600,
  aspectRatio: 1,
  aspectRatioTolerance: 0.1,
  tips: [
    { icon: "utensils", key: "productImage.upload.guidelines.centered" },
    { icon: "eye", key: "productImage.upload.guidelines.light" },
  ],
  guidelinesTitleKey: "productImage.upload.guidelinesTitle",
  dropHintKey: "productImage.upload.dropHint",
};

export type ImageIssue =
  | { level: "error"; key: "fileType" | "fileTooLarge" | "unreadable" }
  | { level: "warning"; key: "lowResolution"; width: number; height: number }
  | { level: "warning"; key: "aspectRatio"; ratio: string };

export interface ImageCheck {
  width: number | null;
  height: number | null;
  issues: ImageIssue[];
}

const greatestCommonDivisor = (a: number, b: number): number =>
  b === 0 ? a : greatestCommonDivisor(b, a % b);

/** "1600 x 900" → "16:9" */
export const formatAspectRatio = (width: number, height: number) => {
  const divisor = greatestCommonDivisor(width, height);
  const w = width / divisor;
  const h = height / divisor;
  return w > 50 || h > 50 ? (width / height).toFixed(2) + ":1" : `${w}:${h}`;
};

const readDimensions = (url: string) =>
  new Promise<{ width: number; height: number } | null>((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });

/** Validates a selected file against the guidelines. Errors block the upload, warnings do not. */
export const checkImage = async (
  file: File,
  previewUrl: string,
  g: ImageGuidelines
): Promise<ImageCheck> => {
  const issues: ImageIssue[] = [];

  if (!g.acceptedTypes.includes(file.type)) {
    return { width: null, height: null, issues: [{ level: "error", key: "fileType" }] };
  }
  if (file.size > g.maxFileSize) {
    issues.push({ level: "error", key: "fileTooLarge" });
  }

  const dimensions = await readDimensions(previewUrl);
  if (!dimensions) {
    return { width: null, height: null, issues: [{ level: "error", key: "unreadable" }] };
  }

  const { width, height } = dimensions;
  if ((g.minWidth && width < g.minWidth) || (g.minHeight && height < g.minHeight)) {
    issues.push({ level: "warning", key: "lowResolution", width, height });
  }
  if (g.aspectRatio && Math.abs(width / height - g.aspectRatio) > (g.aspectRatioTolerance ?? 0)) {
    issues.push({ level: "warning", key: "aspectRatio", ratio: formatAspectRatio(width, height) });
  }

  return { width, height, issues };
};
